import React, { useState } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { logoGoogle } from 'ionicons/icons';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabase';

// UI Library imports
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { TextInput } from '../components/ui/TextInput';
import { Segment, type SegmentOption } from '../components/ui/Segment';

import './LoginPage.css';

type AuthMode = 'login' | 'signup';

const AUTH_MODE_OPTIONS: SegmentOption<AuthMode>[] = [
  { value: 'login', label: 'Sign In' },
  { value: 'signup', label: 'Create Account' },
];

const LoginPage: React.FC = () => {
  const { signIn, signUp } = useAuth();

  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    setMessage(null);

    // Validation
    if (!email.trim() || !password.trim()) {
      setError('Please enter email and password');
      return;
    }

    if (mode === 'signup') {
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      if (password.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }
    }

    setLoading(true);

    if (mode === 'login') {
      const { error } = await signIn(email, password);
      if (error) {
        setError(error);
      }
    } else {
      const { error } = await signUp(email, password, fullName || undefined);
      if (error) {
        setError(error);
      } else {
        setMessage('Check your email to confirm your account!');
        setMode('login');
      }
    }

    setLoading(false);
  };

  const handleModeChange = (newMode: AuthMode) => {
    setMode(newMode);
    setError(null);
    setMessage(null);
  };

  // Google Sign In
  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) {
        setError(error.message);
      }
    } catch (err) {
      setError('Failed to sign in with Google');
    } finally {
      setLoading(false);
    }
  };

  return (
    <IonPage>
      <IonContent className="login-page">
        <div className="login-container">
          {/* Logo / Branding */}
          <div className="login-header">
            <div className="login-logo">🏠</div>
            <h1 className="login-title">Family Hub</h1>
            <p className="login-subtitle">Your family command center</p>
          </div>

          {/* Auth Card */}
          <Card className="login-card">
            {/* Mode Selector */}
            <div style={{ marginBottom: 24 }}>
              <Segment
                options={AUTH_MODE_OPTIONS}
                value={mode}
                onChange={handleModeChange}
                size="md"
              />
            </div>

            {/* Error/Message Display */}
            {error && (
              <div className="login-error">
                {error}
              </div>
            )}
            {message && (
              <div className="login-message">
                {message}
              </div>
            )}

            {/* Form */}
            <div className="login-form">
              {mode === 'signup' && (
                <TextInput
                  label="Full Name"
                  value={fullName}
                  onChange={setFullName}
                  placeholder="Your name"
                  variant="default"
                  disabled={loading}
                />
              )}

              <TextInput
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="you@example.com"
                variant="default"
                disabled={loading}
              />

              <TextInput
                label="Password"
                type="password"
                value={password}
                onChange={setPassword}
                placeholder="Enter password"
                variant="default"
                disabled={loading}
              />

              {mode === 'signup' && (
                <TextInput
                  label="Confirm Password"
                  type="password"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  placeholder="Confirm password"
                  variant="default"
                  disabled={loading}
                />
              )}

              <Button
                variant="primary"
                expand="block"
                onClick={handleSubmit}
                disabled={loading}
                style={{ marginTop: 8 }}
              >
                {loading
                  ? (mode === 'login' ? 'Signing in...' : 'Creating account...')
                  : (mode === 'login' ? 'Sign In' : 'Create Account')
                }
              </Button>

              {/* Divider */}
              <div className="login-divider">
                <span>or</span>
              </div>

              {/* Google Sign In */}
              <Button
                fill="outline"
                expand="block"
                iconStart={logoGoogle}
                onClick={handleGoogleSignIn}
                disabled={loading}
              >
                Continue with Google
              </Button>
            </div>

            {/* Footer */}
            {mode === 'login' && (
              <div className="login-footer">
                <button
                  className="login-link"
                  onClick={() => {/* TODO: Forgot password flow */}}
                >
                  Forgot password?
                </button>
              </div>
            )}
          </Card>

          {/* Terms */}
          <p className="login-terms">
            By continuing, you agree to our Terms of Service and Privacy Policy.
          </p>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default LoginPage;
